import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ActionOrigin, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL, {clearNavigationWarnings} from 'lib/navigateToURL';
import {MoveToTemplateCollectionModal} from 'notebook/entry-templates/collections/MoveToTemplateCollectionModal';
import ProcedureErrorToolTip from 'procedures/design/error-panel/ProcedureErrorToolTip';
import MethodSelectDropdown from 'procedures/design/MethodSelectDropdown';
import DuplicateProcedureModal, {
  DuplicateProcedureModalProps,
} from 'procedures/design/modals/DuplicateProcedureModal';
import {ProcedureHistoryPanelButton} from 'procedures/design/ProcedureHistoryPanel';
import {PROCEDURE_HEADER_HEIGHT} from 'procedures/design/procedureMethodNodeCommonConsts';
import ProcedureNameEditor from 'procedures/design/ProcedureNameEditor';
import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {
  ProcedurePublishingRecordsForButton,
  ProcedurePublishingRecordsForButtonVariables,
} from 'procedures/graphql/__generated/ProcedurePublishingRecordsForButton';
import {PROCEDURE_PUBLISHING_RECORDS_FOR_BUTTON} from 'procedures/graphql/ProceduresQueries';
import {
  createNewDraftProcedureVersion,
  publishProcedureVersion,
  withdrawProcedureVersion,
} from 'procedures/network/ProcedureDAO';
import {procedurePageUrl} from 'procedures/network/procedureUrls';
import {
  trackSwitchedMethodConfiguration,
  trackToggledProcedureViewEvent,
} from 'procedures/procedureAnalyticsEventsUtil';
import {MethodNode, ProcedureMetadata} from 'procedures/ProcedureDomainTypes';
import {
  BlockingSaveObjectSource,
  BlockingSaveOperation,
  ProcedurePageContext,
} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {getStudyTypeForProcedureType} from 'studies/TypedStudyUtils';
import {ellipsisText} from 'styles/Utilities';
import {PublishingRecordForButton} from 'template-collectibles/publishing-ui/graphql/types';
import TemplatePublishingButton from 'template-collectibles/publishing-ui/TemplatePublishingButton';
import {getPublishableInfoForPublishingButton} from 'template-collectibles/publishing-ui/TemplatePublishingUtil';
import Badge, {BadgeColor} from 'ui-library/Badge';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT, hexToRgba, RED, SILVER, WHITE} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import {SaveState, SaveStatusIndicator} from 'ui-library/SaveStatusIndicator';
import {withLoadingScreen} from 'util/Promises';

import NewStudyDropdown from '../../studies/sidepanel/NewStudyDropdown';
import useUnsavedTabChangesCheck from './useUnsavedTabChangesCheck';

interface MenuItemFactoryParams extends ProcedureEditorHeaderProps {
  isMovable: boolean | null;
  isArchivable: boolean | null;
  isUnarchivable: boolean | null;
  isProcedureArchived: boolean;
  isViewingLatestVersion: boolean;
  isSaving: boolean;
  isProcedureDirty: boolean;
  procedureType: ProcedureType;
  runUnsavedDefinitionsChangesCheck: (callbackFn: (didSave: boolean) => void) => void;
}
const overflowMenuItemFactory = (factoryParams: MenuItemFactoryParams): Array<OverflowMenuOption> => {
  const hasUnsavedChanges = factoryParams.isSaving || factoryParams.isProcedureDirty;
  const moveMenuOption: Array<OverflowMenuOption> =
    !factoryParams.isProcedureReadOnly &&
    isFeatureEnabledForProcedureType(factoryParams.procedureType, ProceduresFeature.MOVE) &&
    factoryParams.isMovable
      ? [
          {
            icon: 'move',
            contents: <span>Move</span>,
            onSelect: () => {
              factoryParams.runUnsavedDefinitionsChangesCheck(() => {
                runModal(MoveToTemplateCollectionModal, {
                  title: `Move ${getProcedureUIString(
                    ProceduresStringKeys.PROCEDURE,
                    factoryParams.procedureMetadata.procedureType,
                    {stringCase: StringCase.LOWER}
                  )}`,
                  onRequestSubmit: factoryParams.onMove,
                  onRequestClose: noop,
                  initialTemplateCollectionId: factoryParams.procedureMetadata.templateCollectionId,
                  allowedOwnerIds:
                    // If the procedure is already in a org-owned template collection, it must stay in the same org.
                    factoryParams.procedureMetadata.templateCollectionOwner.type ===
                    ModelTypeConstants.ORGANIZATION
                      ? [factoryParams.procedureMetadata.templateCollectionOwner.id]
                      : undefined,
                });
              });
            },
            isDisabled: hasUnsavedChanges,
          },
        ]
      : [];

  const auditMenuOption: Array<OverflowMenuOption> = isFeatureEnabledForProcedureType(
    factoryParams.procedureType,
    ProceduresFeature.EXPORT
  )
    ? [
        {
          icon: 'audit-trail',
          contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
          onSelect: () => {
            AuditActions.exportAuditLogs([factoryParams.procedureMetadata.id]);
          },
          isDisabled: hasUnsavedChanges || !factoryParams.isViewingLatestVersion,
        },
      ]
    : [];

  const duplicateMenuOption: Array<OverflowMenuOption> = isFeatureEnabledForProcedureType(
    factoryParams.procedureType,
    ProceduresFeature.CREATE
  )
    ? [
        {
          icon: 'copy',
          contents: <span>Duplicate</span>,
          onSelect: () => {
            factoryParams.runUnsavedDefinitionsChangesCheck(() => {
              runModal<DuplicateProcedureModalProps>(DuplicateProcedureModal, {
                sourceProcedureName: factoryParams.procedureMetadata.name,
                sourceProcedureTemplateCollectionId: factoryParams.procedureMetadata.templateCollectionId,
                sourceProcedureType: factoryParams.procedureType,
                onRequestClose: noop,
                onRequestSubmit: factoryParams.onDuplicate,
              });
            });
          },
          isDisabled: hasUnsavedChanges || !factoryParams.isViewingLatestVersion,
        },
      ]
    : [];

  const archiveMenuOption = ((): Array<OverflowMenuOption> => {
    if (!isFeatureEnabledForProcedureType(factoryParams.procedureType, ProceduresFeature.ARCHIVE)) {
      return [];
    } else {
      if (factoryParams.isArchivable && !factoryParams.isProcedureArchived) {
        return [
          {
            icon: 'archive',
            contents: <span>Archive</span>,
            onSelect: () => {
              factoryParams.runUnsavedDefinitionsChangesCheck(() => {
                ArchiveModal.open({
                  items: [factoryParams.procedureMetadata],
                  onArchive: factoryParams.onArchive,
                });
              });
            },
            isDisabled: hasUnsavedChanges,
          },
        ];
      }

      if (
        factoryParams.isUnarchivable &&
        factoryParams.isProcedureArchived &&
        isFeatureEnabledForProcedureType(factoryParams.procedureType, ProceduresFeature.UNARCHIVE)
      ) {
        return [
          {
            icon: 'unarchive',
            contents: <span>Unarchive</span>,
            onSelect: () => {
              factoryParams.onUnarchive();
            },
            isDisabled: hasUnsavedChanges,
          },
        ];
      }
    }

    return [];
  })();
  return [...duplicateMenuOption, ...moveMenuOption, ...archiveMenuOption, ...auditMenuOption];
};

function useReasonPlanExperimentIsBlocked(
  numErrors: number,
  isEditingMethodTab: boolean,
  accessLevel: ProcedureAccessLevel | null,
  procedureType: ProcedureType,
  isViewingEffectiveVersion: boolean
): string | null {
  return useMemo<string | null>(() => {
    const procedureTypeNameSingular = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.LOWER,
    });

    if (!isFeatureEnabledForProcedureType(procedureType, ProceduresFeature.PLAN_EXPERIMENT)) {
      const procedureTypeName = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
        stringCase: StringCase.SENTENCE,
        plural: true,
      });
      const studyType =
        procedureType === ProcedureType.RECIPE ? 'a process development' : 'an assay development';
      return `You do not have sufficient permissions to use ${procedureTypeName}, which is required to create ${studyType} study. Please contact your administrator.`;
    }

    if (!isFeatureEnabled({feature: ProcessesFeature.PROCESSES_CREATE})) {
      return `You do not have sufficient permissions to use Workflows, which is required to create a process development study. Please contact your administrator.`;
    }

    if (numErrors > 0) {
      return `Fix all errors in this ${procedureTypeNameSingular} to create a study.`;
    }

    if (isEditingMethodTab) {
      return `Submit changes to your definitions to create a study.`;
    }

    if (accessLevel !== ProcedureAccessLevel.FULL_ACCESS) {
      // This probably means the procedure links to schemas that you don't have access to (e.g., for a material output configuration)
      return `You do not have sufficient access to this procedure to create a study. Please contact your administrator.`;
    }

    if (BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING) {
      if (!isViewingEffectiveVersion) {
        return `To create a study, please select an effective version of this ${procedureTypeNameSingular}.`;
      }
    }

    return null;
  }, [numErrors, isEditingMethodTab, accessLevel, procedureType, isViewingEffectiveVersion]);
}

function usePublishingRecordsForButton(procedureId: string): Array<PublishingRecordForButton> | null {
  const result = useBenchlingQuery<
    ProcedurePublishingRecordsForButton,
    ProcedurePublishingRecordsForButtonVariables
  >(PROCEDURE_PUBLISHING_RECORDS_FOR_BUTTON, {variables: {procedureId}});
  return result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.procedure)
    ? accessibleObjs(result.data.procedure.publishingRecords)
    : null;
}

interface ProcedureEditorHeaderProps {
  procedureMetadata: ProcedureMetadata;
  methodNodes: Array<MethodNode>;
  onDuplicate: (params: {
    name: string;
    templateCollectionId: string;
    procedureType: ProcedureType;
  }) => Promise<void>;
  onRename: (newName: string) => Promise<void>;
  onMove: (templateCollectionId: string) => void;
  onArchive: (itemIds: Array<string>, purpose: string, options: ArchiveOptionsForModal) => void;
  onUnarchive: () => void;
  onAddMethod: () => void;
  currentFlowchartVersionMetadata: {id: string; createdAt: string; isLatest: boolean};
  onSwitchVersionId: (versionId: string) => void;
  hardRefresh: () => void;
  isProcedureReadOnly: boolean;
}

export default function ProcedureEditorHeader(props: ProcedureEditorHeaderProps): JSX.Element {
  const {methodNodes} = props;
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const {
    isProcedureDirty,
    isSaving,
    saveImmediately,
    selectedMethodCid,
    setSelectedMethodCid,
    isEditingMethodTab,
    accessLevel,
    procedureId,
    procedureType,
  } = ProcedurePageContext.useContext();
  const runUnsavedDefinitionsChangesCheck = useUnsavedTabChangesCheck();
  const publishingRecordsForButton = usePublishingRecordsForButton(procedureId);
  const isViewingEffectiveVersion =
    publishingRecordsForButton?.find(
      (record) => record.publishableVersionId === props.currentFlowchartVersionMetadata.id
    )?.publishingStatus === 'EFFECTIVE';

  const selectedMethod =
    selectedMethodCid != null ? methodNodes.find((methodNode) => methodNode.cid === selectedMethodCid) : null;

  const reasonPlanExperimentIsBlocked = useReasonPlanExperimentIsBlocked(
    procedureValidationErrors.length,
    isEditingMethodTab,
    accessLevel,
    procedureType,
    isViewingEffectiveVersion
  );

  const isProcedureArchived = isArchived(props.procedureMetadata);
  const enableCreateStudyButton =
    procedureValidationErrors.length === 0 && !isEditingMethodTab && reasonPlanExperimentIsBlocked == null;

  const onChangeMethodSelection = useCallback(
    (newSelectedMethodCid: string | null) => {
      const selectMethodCidCallback = (): void => {
        setSelectedMethodCid(newSelectedMethodCid);
        const newSelectedMethod = methodNodes.find(({cid}) => cid === newSelectedMethodCid);
        assert(newSelectedMethod != null, 'Selected unit operation must exist');
        const {id: methodId, cid: methodCid, nodeId} = newSelectedMethod;
        trackSwitchedMethodConfiguration(procedureId, procedureType, methodId, methodCid, nodeId);
      };
      runUnsavedDefinitionsChangesCheck(selectMethodCidCallback);
    },
    [runUnsavedDefinitionsChangesCheck, setSelectedMethodCid, methodNodes, procedureId, procedureType]
  );

  const onCloseMethodEditor = useCallback((): void => {
    runUnsavedDefinitionsChangesCheck(() => {
      clearNavigationWarnings();
      if (selectedMethodCid != null) {
        // Only track event when we've actually switched back to the flowchart view
        trackToggledProcedureViewEvent(procedureId, procedureType, ActionOrigin.PROCEDURE_DESIGNER_DETAILS);
      }
      setSelectedMethodCid(null);
    });
  }, [
    procedureId,
    runUnsavedDefinitionsChangesCheck,
    selectedMethodCid,
    setSelectedMethodCid,
    procedureType,
  ]);

  const {
    movable: isMovable,
    archivable: isArchivable,
    unarchivable: isUnarchivable,
  } = props.procedureMetadata.permissions;

  const saveStatus = isSaving || isProcedureDirty ? SaveState.SAVING : SaveState.SAVED;

  const maybeSaveImmediately =
    (operation: BlockingSaveOperation): (() => Promise<void>) =>
    async (): Promise<void> => {
      if (isProcedureDirty) {
        await saveImmediately(() => ({
          blockingSaveObjectSource: BlockingSaveObjectSource.Procedure,
          blockingSaveOperation: operation,
        }));
      }
    };

  const refreshAfterPublishingChange = (): void => {
    // This will trigger a full reload of all data (procedure, version, publishing records)
    props.hardRefresh();
    // Ensure we reset the URL, in case the user was creating a draft from an old version
    navigateToURL(procedurePageUrl(props.procedureMetadata.id), {skipTrigger: true});
  };

  const handlePublish = async (versionName: string, versionDescription?: string): Promise<void> => {
    assert(BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING, 'Publishing must be enabled');
    await maybeSaveImmediately(BlockingSaveOperation.SaveOnPublishOperation)();
    await publishProcedureVersion(
      props.procedureMetadata.id,
      props.currentFlowchartVersionMetadata.id,
      versionName,
      versionDescription
    );
    refreshAfterPublishingChange();
  };

  const handleWithdraw = async (): Promise<void> => {
    // When withdrawing a published version, no need to pre-save, as the procedure is read only
    assert(
      props.isProcedureReadOnly,
      'Withdrawing a procedure requires a published version, which should be read-only'
    );
    assert(BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING, 'Publishing must be enabled');
    await withdrawProcedureVersion(props.procedureMetadata.id, props.currentFlowchartVersionMetadata.id);
    refreshAfterPublishingChange();
  };

  const handleCreateNewDraft = async (flowchartVersionApiIdentifier: string): Promise<void> => {
    assert(BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING, 'Publishing must be enabled');
    await createNewDraftProcedureVersion(props.procedureMetadata.id, flowchartVersionApiIdentifier);
    refreshAfterPublishingChange();
  };

  return (
    <div className={css(styles.headerBar)}>
      <div className={css(styles.leftGroup)}>
        <div className={css(styles.nameAndIconGroup)}>
          <BenchlingIcon className={css(styles.folderIcon)} icon={FolderItemIcon.Recipe} />
          <div className={css(styles.name)}>
            {!props.isProcedureReadOnly ? (
              <ProcedureNameEditor
                name={props.procedureMetadata.name}
                onRename={props.onRename}
                disableEditsWithTooltip={
                  isEditingMethodTab ? "Finish editing unit operation's definitions" : null
                }
              />
            ) : (
              // TODO: BNCH-90283 tooltip should only be enabled when name overflows, and should not scroll
              <Tooltip text={props.procedureMetadata.name} placement={FloatingPlacement.BOTTOM_START}>
                <div className={css(styles.archivedProcedureNameAndLabel)}>
                  <div className={css(styles.name)}>
                    {props.procedureMetadata.name}
                    {isArchived(props.procedureMetadata) && (
                      <ArchiveRecordLabel
                        item={{
                          id: props.procedureMetadata.id,
                          archiveData: props.procedureMetadata.archiveData,
                          name: props.procedureMetadata.name,
                        }}
                      />
                    )}
                  </div>
                </div>
              </Tooltip>
            )}
          </div>
          {BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE && (
            <Badge
              startingIcon={ModelTypeConstants.TEMPLATE_COLLECTION}
              color={BadgeColor.COLLECTIBLE_BLUE}
              className={css(styles.templateBadge)}
            >
              Template
            </Badge>
          )}
          {!props.isProcedureReadOnly && (
            <SaveStatusIndicator
              saveStatus={saveStatus}
              dataTestAttrs={{component: 'ProcedureEditorHeader', element: 'saveStateIndicator'}}
            />
          )}
        </div>
        <div className={css(styles.headerNameDisplay)}>
          <Button
            onClick={onCloseMethodEditor}
            buttonStyle={ButtonStyle.TRANSPARENT}
            size={ButtonSize.MEDIUM}
            className={css(styles.flowchartButton)}
            text="Flowchart"
            dataTestAttrs={{component: 'ProcedureEditorHeader', element: 'flowchartButton'}}
          />
          <BenchlingIcon icon="chevron" className={css(styles.chevronIcon)} />
          {selectedMethod && (
            <>
              <div className={css(styles.edit)}>Edit</div>
              <MethodSelectDropdown
                selectedMethod={selectedMethod}
                methodNodes={methodNodes}
                onChangeMethodSelection={onChangeMethodSelection}
                onAddMethod={props.onAddMethod}
              />
            </>
          )}
        </div>
      </div>
      <div className={css(styles.rightGroup)}>
        <div className={css(styles.buttonGroup)}>
          <ProcedureErrorToolTip methodNodes={methodNodes} />
          {BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING && (
            <>
              {publishingRecordsForButton && (
                <ProcedurePublishingButton
                  publishingRecords={publishingRecordsForButton}
                  currentFlowchartVersionMetadata={props.currentFlowchartVersionMetadata}
                  onPublish={async (versionName, versionDescription) => {
                    await withLoadingScreen(handlePublish(versionName, versionDescription), 'Publishing...');
                  }}
                  onWithdraw={async () => {
                    await withLoadingScreen(handleWithdraw(), 'Withdrawing...');
                  }}
                  onCreateDraft={async () => {
                    await withLoadingScreen(
                      handleCreateNewDraft(props.currentFlowchartVersionMetadata.id),
                      'Creating draft...'
                    );
                  }}
                  procedureType={procedureType}
                />
              )}
              <ProcedureHistoryPanelButton
                beforeOpeningPanel={maybeSaveImmediately(BlockingSaveOperation.OpenHistoryPanel)}
                onCreateDraft={async (flowchartVersionApiIdentifier: string) => {
                  await withLoadingScreen(
                    handleCreateNewDraft(flowchartVersionApiIdentifier),
                    'Creating draft...'
                  );
                }}
                isProcedureDirty={isProcedureDirty}
              />
            </>
          )}
          <Popover
            placement={FloatingPlacement.TOP}
            renderContent={() => (
              <div className={css(styles.disablePlanExperimentPopover)}>{reasonPlanExperimentIsBlocked}</div>
            )}
            isDisabled={reasonPlanExperimentIsBlocked == null}
          >
            <NewStudyDropdown
              actionOrigin={ActionOrigin.PROCEDURE_EXPERIMENT_DESIGNER}
              studyType={getStudyTypeForProcedureType(props.procedureMetadata.procedureType)}
              studyParams={{initialProcedureId: props.procedureMetadata.id}}
              buttonClassName={css(styles.button)}
              buttonSize={ButtonSize.MEDIUM}
              buttonText="Create study"
              buttonDisabled={!enableCreateStudyButton}
              showCaret={true}
              waitForSave={maybeSaveImmediately(BlockingSaveOperation.SaveOnBeginCreateStudy)}
            />
          </Popover>
          <OverflowMenu
            options={overflowMenuItemFactory({
              ...props,
              isMovable,
              isArchivable,
              isUnarchivable,
              isProcedureArchived,
              isSaving,
              isProcedureDirty,
              isViewingLatestVersion: props.currentFlowchartVersionMetadata.isLatest,
              procedureType,
              runUnsavedDefinitionsChangesCheck,
            })}
            menuProps={{dropLeft: true}}
            buttonProps={{
              size: ButtonSize.MEDIUM,
              dataTestAttrs: {component: 'ProcedureEditorHeader', element: 'overflowMenu'},
            }}
          />
        </div>
      </div>
    </div>
  );
}

// @package Exported for testing
export function ProcedurePublishingButton(props: {
  publishingRecords: Array<PublishingRecordForButton>;
  currentFlowchartVersionMetadata: {id: string; createdAt: string; isLatest: boolean};
  onCreateDraft: () => void;
  onPublish: (versionName: string, versionDescription?: string) => void;
  onWithdraw: () => void;
  procedureType: ProcedureType;
}): JSX.Element {
  assert(BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING, 'Publishing is not enabled for procedures');
  const {
    id: flowchartVersionId,
    createdAt: flowchartVersionCreatedAt,
    isLatest: flowchartIsLatestVersion,
  } = props.currentFlowchartVersionMetadata;
  const {isProcedureDirty, isSaving} = ProcedurePageContext.useContext();
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();

  const getPublishableInfoCallback = useCallback(
    (publishingRecords) =>
      getPublishableInfoForPublishingButton(
        publishingRecords,
        flowchartVersionId,
        flowchartVersionCreatedAt,
        flowchartIsLatestVersion
      ),
    [flowchartVersionId, flowchartVersionCreatedAt, flowchartIsLatestVersion]
  );

  return (
    <div className={css(styles.publishingButton)}>
      <TemplatePublishingButton
        {...getPublishableInfoCallback(props.publishingRecords)}
        publishingRecords={props.publishingRecords}
        publishableModelTypeName={getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
          stringCase: StringCase.LOWER,
        })}
        onPublish={props.onPublish}
        onWithdraw={props.onWithdraw}
        onCreateDraft={props.onCreateDraft}
        isPublishOptionDisabled={isProcedureDirty || isSaving || procedureValidationErrors.length > 0}
        tooltipPlacement={FloatingPlacement.LEFT}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  headerBar: {
    background: WHITE,
    marginBottom: '4px',
    boxShadow: `0px 2px 4px ${GRAY_LIGHT}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexGrow: 1,
    height: `${PROCEDURE_HEADER_HEIGHT}px`,
    paddingTop: '6px',
    overflow: 'hidden',
  },
  button: {
    marginLeft: 5,
    marginRight: 5,
  },
  publishingButton: {
    marginRight: 5,
    marginTop: 2,
  },
  nameAndIconGroup: {
    display: 'flex',
    gap: '10px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    alignItems: 'center',
    height: '36px',
  },
  name: {
    fontSize: '16px',
    padding: '6px',
    minWidth: '100px',
    ...ellipsisText,
  },
  archivedProcedureNameAndLabel: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 10,
    marginBottom: 10,
    marginRight: 15,
    whiteSpace: 'nowrap',
  },
  folderIcon: {
    height: 18,
    color: SILVER,
  },
  chevronIcon: {
    height: 12,
    marginRight: 10,
    color: SILVER,
  },
  disablePlanExperimentPopover: {
    borderRadius: 3,
    background: WHITE,
    boxShadow: `0px 2px 8px 2px ${hexToRgba(GRAY, 0.8)};`,
    width: '170px',
    textAlign: 'center',
    lineHeight: '14px',
    margin: '-2px 0px',
  },
  saveStateIndicator: {
    display: 'flex',
    alignItems: 'center',
    color: GRAY,
    marginRight: '4px',
  },
  validationErrorIcon: {
    color: RED,
    width: '15px',
    height: '15px',
    marginRight: '8px',
    display: 'flex',
  },
  leftGroup: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '16px',
    paddingRight: '16px',
    overflow: 'hidden',
  },
  rightGroup: {
    display: 'flex',
    marginTop: '-6px',
  },
  headerNameDisplay: {
    display: 'flex',
    height: '32px',
    fontSize: '14px',
    fontWeight: 400,
    alignItems: 'center',
    marginBottom: '4px',
  },
  edit: {
    padding: '0px 8px 1px 0px',
  },
  flowchartButton: {
    padding: '5px 4px 7px 4px',
    marginRight: '6px',
    borderRadius: '2px',
    height: 'auto',
    fontWeight: 400,
    fontSize: '14px',
    ':hover': {
      background: Color.GRAY_100,
    },
  },
  templateBadge: {
    marginLeft: '-15px',
  },
});
